import { useNavigate } from "react-router-dom";
import type { Order, OrderItem } from "@/schemas/order";
import { useOrderPizzas } from "@/orders/hooks/use-order-pizzas";
import { PizzaThumbnail } from "@/components/pizza-thumbnail";
import { StatusDot, statusTone } from "@/components/status-badge";
import { parsePizzaName } from "@/lib/order-items";
import { t as statusText } from "@/lib/i18n";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";

/** The pizzas of the order as a row of thumbnails: the verdict of each, one tap to switch. */
export function OrderPizzaStrip({
  order,
  items,
  currentId,
}: {
  order: Order;
  items: OrderItem[];
  currentId: string | null;
}) {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { pizzasById } = useOrderPizzas(items.map((item) => item.id));

  return (
    <nav
      aria-label={t("detail.strip.label")}
      data-no-swipe
      className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:thin]"
    >
      {items.map((item, index) => {
        const pizza = pizzasById.get(item.id);
        const status = pizza?.evaluation?.metadata?.overall_evaluation?.status;
        const active = item.id === currentId;
        const { name } = parsePizzaName(item.name);
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => navigate(`/orders/${order.id}/pizza/${item.id}`)}
            aria-current={active ? "page" : undefined}
            className={cn(
              "flex w-44 shrink-0 items-center gap-2 rounded-lg border bg-card p-1.5 pr-2.5 text-left transition-colors",
              active
                ? "border-primary/60 bg-secondary ring-1 ring-primary/40"
                : "hover:bg-muted/60",
            )}
          >
            <PizzaThumbnail
              pizzaId={item.id}
              roi={pizza?.evaluation?.metadata?.pizza_roi}
              enabled={!!pizza}
              scale={6}
              className="h-10 w-10 shrink-0 rounded-md"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-semibold tabular-nums text-muted-foreground">
                  {index + 1}/{items.length}
                </span>
                <StatusDot
                  tone={pizza ? statusTone(status) : "neutral"}
                  title={
                    pizza
                      ? status
                        ? statusText(status)
                        : t("status.notEvaluated")
                      : t("detail.strip.notCaptured")
                  }
                />
              </div>
              <div className="truncate text-xs font-medium">{name}</div>
            </div>
          </button>
        );
      })}
    </nav>
  );
}
